import React, { useEffect, useState } from 'react';
import { Plus, ChevronRight, Loader2, AlertCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { semanticBrandingService, BrandDocument } from '../../services/semanticBranding';
const AnalysisCard = ({
  brandDocument,
  isNewCard = false
}) => {
  const navigate = useNavigate();
  // Extract data from brand document if available
  const brand = brandDocument?.marca || '';
  const url = brandDocument?.url || '';
  const step = brandDocument?.step || '';
  const id = brandDocument?.id || '';
  const updatedAt = brandDocument?.updatedAt ? new Date(brandDocument.updatedAt) : null;
  // Extract attributes and competitors if available
  const attributes = brandDocument?.metadata?.atributos?.map(attr => typeof attr === 'object' && attr !== null ? attr.nome : attr) || brandDocument?.atributos || [];
  const competitors = brandDocument?.metadata?.concorrentesSelecionados?.map(comp => typeof comp === 'object' && comp !== null ? comp.marca : comp) || brandDocument?.concorrentes || [];
  // Format the last update date
  const formatDate = date => {
    if (!date) return 'N/A';
    const now = new Date();
    const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return 'hoje';
    if (diffDays === 1) return 'ontem';
    if (diffDays < 7) return `${diffDays} dias atrás`;
    return date.toLocaleDateString('pt-BR');
  };
  const handleCardClick = () => {
    if (isNewCard) {
      // Navigate to top of page to create new analysis
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    } else {
      // Navigate to the report page with the document ID
      navigate(`/relatorio/${id}`);
    }
  };
  if (isNewCard) {
    return <div onClick={handleCardClick} className="p-6 bg-white border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-[#1649ff] hover:shadow-sm transition-all flex flex-col items-center justify-center h-full min-h-[220px]" data-id="element-515">
        <div className="w-12 h-12 rounded-full bg-gray-50 flex items-center justify-center mb-4" data-id="element-516">
          <Plus className="w-6 h-6 text-gray-400" data-id="element-517" />
        </div>
        <p className="text-gray-500 font-medium" data-id="element-518">Criar nova análise</p>
      </div>;
  }
  // Get the favicon based on the URL
  const getFavicon = url => {
    try {
      const domain = new URL(url).hostname;
      return `https://www.google.com/s2/favicons?domain=${domain}&sz=128`;
    } catch (e) {
      return `https://www.google.com/s2/favicons?domain=example.com&sz=128`;
    }
  };
  return <div onClick={handleCardClick} className="p-6 bg-white border border-gray-100 rounded-lg shadow-sm hover:shadow-md transition-shadow cursor-pointer" data-id="element-519">
      <div className="flex items-center mb-4" data-id="element-520">
        <img src={getFavicon(url)} alt={`${brand} logo`} className="w-5 h-5 mr-3" onError={e => {
        e.target.onerror = null;
        e.target.src = 'https://www.google.com/s2/favicons?domain=example.com&sz=128';
      }} data-id="element-521" />
        <div data-id="element-522">
          <h3 className="text-xl font-semibold text-gray-900" data-id="element-523">{brand}</h3>
          {step !== 'analise' && step !== 'concluido' ? <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded-full" data-id="element-524">
              {step === 'inicial' ? 'Inicial' : step === 'concorrentes' ? 'Concorrentes definidos' : step === 'atributos' ? 'Atributos definidos' : ''}
            </span> : <span className="text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded-full" data-id="element-525">
              Análise concluída
            </span>}
        </div>
      </div>
      <div className="space-y-3" data-id="element-526">
        {/* Competitors tags */}
        {competitors.length > 0 && <div data-id="element-527">
            <span className="text-xs font-bold text-gray-500 mb-1 block" data-id="element-528">
              Concorrentes:
            </span>
            <div className="flex flex-wrap max-h-12 overflow-hidden" data-id="element-529">
              {competitors.map((competitor, index) => <span key={index} className="text-xs bg-gray-100 text-gray-700 px-2 py-0.5 rounded-full mr-1 mb-1" data-id="element-530">
                  {typeof competitor === 'string' ? competitor : 'Concorrente'}
                </span>)}
            </div>
          </div>}
        {/* Attributes tags */}
        {attributes.length > 0 && <div data-id="element-531">
            <span className="text-xs font-bold text-gray-500 mb-1 block" data-id="element-532">
              Atributos:
            </span>
            <div className="flex flex-wrap max-h-12 overflow-hidden" data-id="element-533">
              {attributes.map((attribute, index) => <span key={index} className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full mr-1 mb-1" data-id="element-534">
                  {typeof attribute === 'string' ? attribute : 'Atributo'}
                </span>)}
            </div>
          </div>}
      </div>
      <div className="mt-4 pt-4 border-t border-gray-100" data-id="element-535">
        <div className="flex justify-between items-center" data-id="element-536">
          <span className="text-xs text-gray-500" data-id="element-537">
            Última atualização: {formatDate(updatedAt)}
          </span>
          <span className="text-xs font-medium text-[#1649ff]" data-id="element-538">
            Ver relatório
          </span>
        </div>
      </div>
    </div>;
};
const AnalysisCardsSection = ({
  isLoggedIn
}) => {
  // Only show this section for logged-in users
  if (!isLoggedIn) return null;
  const [brandDocuments, setBrandDocuments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  useEffect(() => {
    const fetchBrandDocuments = async () => {
      try {
        setIsLoading(true);
        setError('');
        const response = await semanticBrandingService.listBrandDocuments();
        // Ensure we're working with an array
        const documents = Array.isArray(response.brandDocuments) ? response.brandDocuments : [];
        setBrandDocuments(documents);
      } catch (err) {
        console.error('Failed to fetch brand documents:', err);
        setError('Não foi possível carregar suas análises. Tente novamente mais tarde.');
      } finally {
        setIsLoading(false);
      }
    };
    if (isLoggedIn) {
      fetchBrandDocuments();
    }
  }, [isLoggedIn]);
  return <section className="py-16 bg-white" data-id="element-539">
      <div className="container px-4 mx-auto sm:px-6 lg:px-8" data-id="element-540">
        <div className="flex items-center justify-between mb-8" data-id="element-541">
          <h2 className="text-2xl font-bold text-gray-900" data-id="element-542">
            Suas análises semânticas
          </h2>
          {brandDocuments.length > 0 && <button className="text-sm font-medium text-[#1649ff] hover:underline" data-id="element-543">
              Ver todas
            </button>}
        </div>
        {isLoading ? <div className="flex justify-center items-center py-12" data-id="element-544">
            <Loader2 size={24} className="animate-spin text-[#1649ff]" data-id="element-545" />
            <span className="ml-2 text-gray-600" data-id="element-546">Carregando análises...</span>
          </div> : error ? <div className="bg-red-50 p-4 rounded-lg text-red-700 flex items-center" data-id="element-547">
            <AlertCircle size={20} className="mr-2 flex-shrink-0" data-id="element-548" />
            <p data-id="element-549">{error}</p>
          </div> : <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3" data-id="element-550">
            {brandDocuments.map(document => <AnalysisCard key={document.id || index} brandDocument={document} data-id="element-551" />)}
            {/* "Create new" card */}
            <AnalysisCard isNewCard={true} data-id="element-552" />
            {brandDocuments.length === 0 && <div className="col-span-full bg-blue-50 p-4 rounded-lg text-blue-700" data-id="element-553">
                <p className="text-center" data-id="element-554">
                  Você ainda não possui análises. Crie uma nova análise para
                  começar.
                </p>
              </div>}
          </div>}
      </div>
    </section>;
};
export default AnalysisCardsSection;